<template>
  <div class="page-table scrollable only-y">
    <!-- DIDs Table -->
    <div class="no-table-box page-header">
      <el-row>
        <el-col :span="12">
          <h1 class="pb-15">
            <v-svg class="voxo-aqua" sprite="phone-number" />
            Phone Numbers
          </h1>
        </el-col>
      </el-row>

      <div class="flex pb-20">
        <div class="box">
          <el-input clearable placeholder="Search" v-model="search" />
        </div>
        <el-button class="box right" @click="createVisible = true">
          Add New
        </el-button>
        <el-button
          class="table-button-bulk hidden-xs-only"
          @click="bulkCreateVisible = true"
        >
          Bulk Create
        </el-button>
      </div>

      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <!-- Create Modal -->
    <el-dialog
      id="create-modal"
      class="form-modal"
      v-loading="createProcessing"
      :visible.sync="createVisible"
      :fullscreen="true"
      :show-close="false"
      :modal="false"
      @closed="clearFormFields(createForm)"
    >
      <div id="create-modal-header" class="modal-header">
        <div class="modal-header-layout flex grid-6 align-center">
          <h1 class="modal-title col-2">Create Number</h1>
          <div class="modal-actions col-2">
            <el-button type="primary" @click="createNumber">Create</el-button>
            <el-button type="default" @click="cancelOperation"
              >Cancel</el-button
            >
          </div>
        </div>
      </div>

      <el-form>
        <div class="inline-form-group">
          <el-input v-model="createForm.number" placeholder="Enter number">
            <template slot="prefix">Phone number</template>
          </el-input>
        </div>

        <div class="inline-form-group">
          <el-col>
            <dads-select
              v-model="createForm.tenantId"
              :value="createForm.tenantId"
              :remote-method="remoteSearchTenants"
              v-loading="tenantsLoading"
              placeholder="Assign to tenant"
              remote
              filterable
            >
              <template slot="prefix">Tenant</template>

              <el-option
                v-for="tenant in tenantOptions"
                :key="tenant.id"
                :label="
                  tenant.id === originalTenantId
                    ? `${tenant.name} (current)`
                    : tenant.name
                "
                :value="tenant.id"
              />
            </dads-select>
          </el-col>
        </div>
      </el-form>
    </el-dialog>

    <!-- Edit Modal -->
    <el-dialog
      class="form-modal"
      id="edit-model"
      v-loading="editProcessing"
      @closed="clearFormFields(editForm)"
      :fullscreen="true"
      :modal="false"
      :show-close="false"
      :visible.sync="editVisible"
    >
      <div id="edit-modal-header" class="modal-header">
        <div class="modal-header-layout flex grid-6 align-center">
          <h1 class="modal-title col-2">Edit Number</h1>
          <div class="modal-actions col-2">
            <el-button type="primary" @click="updateNumber">Save</el-button>
            <el-button type="default" @click="cancelOperation"
              >Cancel</el-button
            >
          </div>
        </div>
      </div>

      <el-form>
        <recent-audits service="Phone Number" :record="currentRecord" />
        <div class="inline-form-group">
          <el-col :span="9">
            <el-input
              v-model="editForm.number"
              placeholder="Enter number"
              :readOnly="true"
              :disabled="true"
            >
              <template slot="prefix">Phone number</template>
            </el-input>
          </el-col>

          <el-col :span="1"></el-col>

          <el-col :span="14">
            <el-input
              :value="editForm.callerId"
              @input="editForm.callerId = sanitizeCallerId($event)"
              placeholder="Enter caller id name"
              v-loading="findingCallerId"
            >
              <template slot="prefix">Caller id name</template>
            </el-input>
          </el-col>
        </div>

        <div class="inline-form-group">
          <el-col :span="14">
            <el-input
              v-model="editForm.namePrefix"
              placeholder="Enter prefix name"
            >
              <template slot="prefix">Prefix name</template>
            </el-input>
          </el-col>

          <el-col :span="1"></el-col>

          <el-col :span="9">
            <el-select
              v-model="editForm.recording"
              :value="editForm.recording"
              placeholder="Select option"
            >
              <template slot="prefix">Allow recording</template>

              <el-option
                v-for="(option, index) in recordingOptions"
                :key="index"
                :label="option"
                :value="option"
              />
            </el-select>
          </el-col>
        </div>

        <div class="inline-form-group">
          <el-col>
            <dads-select
              v-model="editForm.tenantId"
              :value="editForm.tenantId"
              :remote-method="remoteSearchTenants"
              v-loading="tenantsLoading"
              placeholder="Assign to tenant"
              remote
              filterable
            >
              <template slot="prefix">Tenant</template>

              <el-option
                v-for="tenant in tenantOptions"
                :key="tenant.id"
                :label="
                  tenant.id === originalTenantId
                    ? `${tenant.name} (current)`
                    : tenant.name
                "
                :value="tenant.id"
              />
            </dads-select>
          </el-col>
        </div>
      </el-form>
    </el-dialog>

    <bulk-create
      :visible="bulkCreateVisible"
      :numberFormatter="numberFormatter"
      @modalClosed="bulkCreateVisible = false"
    />
  </div>
</template>

<script>
import axios from "axios";
import debounce from "lodash/debounce";
import { mapActions } from "vuex";
import stateList from "../../../ref/states";
import DadsSelect from "@/components/select";
import RecentAudits from "@/components/RecentAudits";
import feathersClient, { apiV2Host } from "../../../feathers/feathers-client";
import { phoneNumberFormatter } from "@/globals/globals";
import BulkCreate from "./components/bulk-create.vue";

export default {
  name: "TenantPhoneNumbers",

  components: { DadsSelect, RecentAudits, BulkCreate },

  data() {
    return {
      tenantsQuery: null,
      tenantsLoading: false,
      tenantOptions: [],

      bulkCreateVisible: false,
      branchOptions: [],
      tableData: [],
      areaOptions: "",
      createProcessing: false,
      createVisible: false,
      deleteProcessing: false,
      editProcessing: false,
      editVisible: false,
      findingCallerId: false,
      numbersLoading: false,
      numberOptions: [],
      optionsLoading: false,
      originalCallerId: "",
      originalTenantId: "",
      rateCenters: "",
      recordingOptions: ["yes", "no"],
      state: stateList,
      visibleActionsId: "",
      tableLoading: false,
      modalModelId: null,
      currentRecord: null,
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: ({ row }) => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-aqua" sprite="phone-number" />
                </div>
              ];
            }
          }
        },
        {
          field: "number",
          title: "Number",
          minWidth: 150,
          sortable: true,
          slots: {
            default: ({ row }) => {
              return [
                <span class="first-table-column">
                  {this.numberFormatter(row.number)}
                </span>
              ];
            }
          }
        },
        {
          field: "tenantName",
          title: "Account",
          minWidth: 150,
          sortable: true
        },
        {
          field: "namePrefix",
          title: "Prefix",
          minWidth: 150,
          sortable: true
        },
        {
          field: "useStatus",
          title: "Status",
          sortable: true
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.getNumberProfile(row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteRecord(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>
              ];
            }
          }
        }
      ],

      // Forms
      createForm: {
        number: "",
        tenantId: ""
      },

      editForm: {
        callerId: "",
        namePrefix: "",
        number: "",
        recording: "",
        tenantId: "",
        branchId: ""
      },

      // Pagination
      limit: 150,
      search: ""
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    }
  },

  watch: {
    search: debounce(function () {
      this.getDidsList();
    }, 500),

    tenantsQuery: debounce(function () {
      this.queryTenantsList();
    }, 500),

    user() {
      this.getDidsList();
      this.queryTenantsList();
    }
  },

  methods: {
    // CNAM is written to cc_cnamecache and sent in outbound SIP headers; some
    // carriers reject special characters, so only allow letters, digits and spaces.
    sanitizeCallerId(value) {
      return (value || "").replace(/[^A-Za-z0-9 ]/g, "");
    },

    ...mapActions("dids", {
      createNumbers: "create",
      patchNumber: "patch",
      removeNumber: "remove"
    }),

    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.getNumberProfile(data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    cancelOperation() {
      this.createVisible = false;
      this.editVisible = false;
      this.clearFormFields(this.createForm);
      this.numberOperations = [];
      this.originalCallerId = "";
      this.originalTenantId = "";
    },

    clearFormFields(form) {
      this.tenantsQuery = null;

      Object.keys(form).forEach((key) => {
        form[key] = typeof form[key] === typeof [] ? [] : "";
      });
    },

    createNumber() {
      // Strip any formatting the admin may have typed (dashes, spaces, parens)
      // so we never persist a non-digit number. A value like "913-555-1234"
      // passes the create length check but later fails the digits-only patch
      // validation, which is the bug this guards against.
      const number = (this.createForm.number || "").replace(/\D/g, "");
      const data = {
        tenantId: this.createForm.tenantId,
        numbers: [number],
        orderType: "MANUAL"
      };

      this.$confirm("Create new number?", "Warning", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      })
        .then(() => {
          this.createProcessing = true;
          this.createNumbers(data)
            .then(() => {
              this.$notify({
                message: "Number successfully created!",
                title: "Success",
                type: "success"
              });
            })
            .catch((error) => {
              if (error.hasOwnProperty("errors")) {
                this.$notify({
                  message: error.errors[Object.keys(error.errors)[0]],
                  title: error.message,
                  type: "error"
                });
              } else {
                this.$notify({
                  message: error.message,
                  title: error.name,
                  type: "error"
                });
              }
            });
        })
        .finally(() => {
          this.createProcessing = false;
          this.createVisible = false;
        })
        .catch(() => true);
    },

    async deleteRecord(id) {
      if (!this.editVisible) {
        this.deleteProcessing = true;
      }

      this.editProcessing = true;

      const didRecord = await feathersClient.service("dids").get(id);
      let message = "";

      if (didRecord.useStatus === "Unavailable") {
        message = `Are you sure? This number is assigned to something in a tenant's account.
            Once you delete this number it will not be able to be retrieved again.`;
      } else {
        message = "Permanently delete this number?";
      }

      this.deleteProcessing = false;
      this.$confirm(message, {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning"
      }).then(() => {
        if (!this.editVisible) {
          this.deleteProcessing = true;
        }

        this.removeNumber(id)
          .then(() => {
            this.$notify({
              message: "Phone number successfully removed!",
              title: "Success",
              type: "success"
            });
            const tableIndex = this.tableData.findIndex(
              (item) => item.id === id
            );
            if (tableIndex >= 0) {
              this.tableData.splice(tableIndex, 1);
            }
            this.editVisible = false;
          })
          .catch((error) => {
            this.$notify({
              message: error.message,
              title: error.name,
              type: "error"
            });
          })
          .finally(() => {
            this.deleteProcessing = false;
            this.editProcessing = false;
          });
      });
    },

    getDidsList() {
      this.tableLoading = true;

      const query = {
        $limit: this.limit,
        allowEmergency: "",
        filter: this.search
      };

      if (+this.user.userRole === 3 || +this.user.userRole === 12) {
        query.tenantId = { $in: this.user.tenantIds };
      }

      //if this is a partner admin let's just grab the first few tenants and let remote search from there.
      if (+this.user.userRole === 11 && !this.search.length) {
        const partnerTenants = [...this.$store.state.authTenants];
        const partnerTenantsLength = partnerTenants.length;
        const queryTenantIds = [];
        if (partnerTenantsLength) {
          for (let i = 0; i < 6; i++) {
            if (i > partnerTenantsLength - 1) {
              break;
            }
            queryTenantIds.push(partnerTenants[i].id);
          }
        }
        query.tenantId = { $in: queryTenantIds };
      }

      //if partner admin is searching, we will check tenant ids on the backend instead.
      if (+this.user.userRole === 11 && this.search.length) {
        query.tenantId = "partnerAdminGlobals";
      }

      feathersClient
        .service("dids")
        .find({
          query
        })
        .then((response) => {
          this.tableData = response;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableLoading = false;
        });
    },

    async getNumberProfile(id) {
      const currentRecord = await feathersClient.service("dids").get(id);

      this.populateFields(currentRecord);
      this.currentRecord = currentRecord;
      this.originalTenantId = this.editForm.tenantId;

      this.editVisible = true;
      this.modalModelId = id;

      if (!currentRecord.registeredCNAM) {
        this.findingCallerId = true;
        const numberOrderingService = feathersClient.service("tn-ordering");
        const params = {
          query: {
            context: "numberDetails",
            number: this.editForm.number
          }
        };

        numberOrderingService
          .find(params)
          .then((response) => {
            this.editForm.callerId = response[0].tnFeature.callerId.callingName;
            this.originalCallerId = response[0].tnFeature.callerId.callingName;
            this.findingCallerId = false;
          })
          .catch((error) => {
            this.findingCallerId = false;
          });
      } else {
        this.editForm.callerId = currentRecord.registeredCNAM;
        this.originalCallerId = currentRecord.registeredCNAM;
      }
    },

    numberFormatter(val) {
      return phoneNumberFormatter(val);
    },

    openEditModal(id) {
      this.modalModelId = id;
      this.editVisible = true;
    },

    populateFields(record) {
      for (let key in record) {
        if (record.hasOwnProperty(key) && this.editForm.hasOwnProperty(key)) {
          this.editForm[key] = record[key];
        }
      }
      this.editForm.recording =
        record.recording === "yeschange" ? "yes" : record.recording;

      this.queryTenantsList();
    },

    updateNumber() {

      this.$confirm("Update phone number details?", "Warning", {
        cancelButtonText: "Cancel",
        confirmButtonText: "Update"
      }).then(() => {
        let data = { ...this.editForm };
        data.callerId = this.sanitizeCallerId(data.callerId);

        data.recording =
          data.recording === "yes" ? "yeschange" : data.recording;

        this.editProcessing = true;

        //if the tenant changed null out the branch id
        if (+data.tenantId !== +this.currentRecord.tenantId) {
          data.branchId = null;
        }

        delete data.number;

        // We need to take the destinations from the currentRecord and pass them along
        const {
          didDestinations,
          smsDestinations,
          unconditionalDestinations,
          fax
        } = this.currentRecord;

        const mapDestinations = (destinations) =>
          destinations.map((destination, index) => ({
            id: destination.id,
            type: destination.type,
            order: index + 1
          }));

        // Map all destination types
        const mappedDidDestinations = mapDestinations(didDestinations);
        const mappedSmsDestinations = mapDestinations(smsDestinations);
        const mappedUnconditionalDestinations = mapDestinations(
          unconditionalDestinations
        );

        // Conditionally add to data object, we need to map what we can so the update works
        data.didDestinations = mappedDidDestinations || [];
        data.smsDestinations = mappedSmsDestinations || [];
        data.unconditionalDestinations = mappedUnconditionalDestinations || [];
        data.fax = fax;

        this.patchNumber([this.modalModelId, data])
          .then((res) => {
            this.$notify({
              message: `${this.numberFormatter(res.number)} has been updated!`,
              title: "Success",
              type: "success"
            });
            this.editVisible = false;
            const tableIndex = this.tableData.findIndex(
              (item) => item.id === this.modalModelId
            );
            if (tableIndex >= 0) {
              this.tableData.splice(tableIndex, 1, res);
            }
          })
          .catch((error) => {
            if (error.hasOwnProperty("errors")) {
              this.$notify({
                message: error.errors[Object.keys(error.errors)[0]],
                title: error.message,
                type: "error"
              });
            } else {
              this.$notify({
                message: error.message,
                title: error.name,
                type: "error"
              });
            }
          })
          .finally(() => {
            this.editProcessing = false;
          });
      });
    },

    async queryTenantsList() {
      const token = this.$store.state.auth.accessToken;
      this.tenantsLoading = true;

      try {
        let url = `${apiV2Host}/admin/tenants`;

        if (this.tenantsQuery) {
          url = `${url}?query=${this.tenantsQuery}`;
        }

        const res = await axios.get(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });
        const { records } = res.data;

        this.tenantOptions = [...records];

        if (this.editForm.tenantId) {
          const searchTenant = this.tenantOptions.find(
            (tenant) => tenant.id === this.editForm.tenantId
          );

          if (!searchTenant) {
            const selectedTenant = await feathersClient
              .service("tenants")
              .get(this.editForm.tenantId);
            this.tenantOptions.push(selectedTenant);
          }
        }

        this.tenantsLoading = false;
      } catch (err) {
        this.tenantsLoading = false;
      }
    },

    remoteSearchTenants(query) {
      this.tenantsQuery = query;
    }
  },

  filters: {
    number(value) {
      return !isNaN(value)
        ? new Intl.NumberFormat("en-US").format(value)
        : value;
    }
  },

  created() {
    this.getDidsList();
    this.queryTenantsList();
  }
};
</script>
